import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Wallet, FileText, CreditCard, TrendingUp,
  ArrowRight, Clock, CheckCircle, AlertCircle,
  AlertTriangle, Ban, Hash,
  Percent, CalendarDays, ShieldCheck, ShieldAlert, ShieldX
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MerchantDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [accountBalance, setAccountBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, invoicesRes, paymentsRes, billingRes, usageRes, outstandingRes, balanceRes] = await Promise.all([
        axios.get(`${API}/payments/stats`),
        axios.get(`${API}/invoices?status=pending`),
        axios.get(`${API}/payments`),
        axios.get(`${API}/billing/status`).catch(() => ({ data: null })),
        axios.get(`${API}/billing/usage/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/billing/outstanding-balance`).catch(() => ({ data: null })),
        axios.get(`${API}/billing/balance`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      const invoicesData = Array.isArray(invoicesRes.data) ? invoicesRes.data : (invoicesRes.data.data || []);
      const paymentsData = Array.isArray(paymentsRes.data) ? paymentsRes.data : (paymentsRes.data.data || []);
      setRecentInvoices(invoicesData.slice(0, 5));
      setRecentPayments(paymentsData.slice(0, 5));
      if (billingRes.data) setBillingStatus(billingRes.data);
      if (usageRes.data) setUsageSummary(usageRes.data);
      if (outstandingRes.data) setOutstanding(outstandingRes.data);
      if (balanceRes.data) setAccountBalance(balanceRes.data.balance ?? null);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally { setLoading(false); }
  };

  // Determine billing status state
  const getBillingState = () => {
    if (!billingStatus) return { state: 'good', label: 'Good Standing', color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', Icon: ShieldCheck };
    if (billingStatus.is_suspended) return { state: 'restricted', label: 'Restricted', color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', Icon: ShieldX };
    if (billingStatus.health === 'overdue' || billingStatus.health === 'grace_period') return { state: 'due', label: 'Payment Due', color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', Icon: ShieldAlert };
    if (billingStatus.health === 'pending') return { state: 'due', label: 'Payment Due', color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', Icon: ShieldAlert };
    return { state: 'good', label: 'Good Standing', color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', Icon: ShieldCheck };
  };

  const billingState = getBillingState();
  const txUsed = usageSummary?.tx_count || 0;
  const txLimit = usageSummary?.tx_limit;
  const txRemaining = txLimit !== null && txLimit !== undefined ? Math.max(0, txLimit - txUsed) : null;
  const txPercent = txLimit ? Math.min(100, (txUsed / txLimit) * 100) : 0;

  const statCards = [
    { icon: CreditCard, label: 'Total Payments', value: stats?.total_payments || 0, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10' },
    { icon: TrendingUp, label: 'USDT Volume', value: `$${(stats?.volume_by_currency?.USDT || 0).toFixed(2)}`, color: 'text-primary', bgColor: 'bg-primary/10' },
    { icon: FileText, label: 'Pending Invoices', value: stats?.pending_invoices || 0, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
    { icon: Wallet, label: 'USDC Volume', value: `$${(stats?.volume_by_currency?.USDC || 0).toFixed(2)}`, color: 'text-blue-500', bgColor: 'bg-blue-500/10' }
  ];

  return (
    <MerchantLayout title="Dashboard">
      <div className="space-y-6" data-testid="merchant-dashboard">
        <p className="text-muted-foreground text-sm -mt-1">
          Welcome back, {user?.full_name || 'Merchant'}! Here's your payment overview.
        </p>

        {/* Billing Alert Banners */}
        {billingStatus?.is_suspended && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="billing-suspended-banner">
            <Ban className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-400">Account Suspended</p>
              <p className="text-xs text-red-400/80 mt-0.5">Your account is suspended due to unpaid billing invoices exceeding 60 days.</p>
              <Link to="/billing"><Button size="sm" className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs mt-2" data-testid="pay-now-dashboard-btn">Pay Outstanding Invoices</Button></Link>
            </div>
            <span className="text-xs font-bold text-red-400">${billingStatus.total_amount_due?.toFixed(2)}</span>
          </div>
        )}

        {!billingStatus?.is_suspended && (billingStatus?.health === 'overdue' || billingStatus?.health === 'grace_period') && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="billing-overdue-banner">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-400">
                {billingStatus.health === 'grace_period' ? `Grace Period — ${billingStatus.grace_period?.days_remaining || 0} days remaining` : 'Payment Overdue'}
              </p>
              <p className="text-xs text-amber-400/80 mt-0.5">
                {billingStatus.health === 'grace_period'
                  ? `Pay by ${billingStatus.grace_period?.grace_period_end ? new Date(billingStatus.grace_period.grace_period_end).toLocaleDateString() : 'soon'} to keep your plan.`
                  : `You have ${billingStatus.overdue_invoices} overdue invoice${billingStatus.overdue_invoices > 1 ? 's' : ''} totaling $${billingStatus.total_amount_due?.toFixed(2)}.`}
              </p>
              <Link to="/billing"><Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs mt-2" data-testid="pay-overdue-dashboard-btn">View & Pay Invoices</Button></Link>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, i) => (
            <Card key={i} className="border-border bg-card">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </div>
                <p className="text-muted-foreground text-[13px] mb-1">{stat.label}</p>
                <p className="text-2xl font-bold font-mono tracking-tight" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Billing Summary Card */}
        {usageSummary?.has_subscription && (
          <Card className={`border ${billingState.border} bg-card`} data-testid="billing-summary-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">Billing Summary</CardTitle>
                <Link to="/billing">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" data-testid="view-billing-btn">
                    View Billing <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Billing Status */}
                <div className={`rounded-xl p-4 ${billingState.bg} flex flex-col items-center justify-center`} data-testid="billing-status-indicator">
                  <billingState.Icon className={`w-7 h-7 ${billingState.color} mb-1`} />
                  <p className={`text-sm font-bold ${billingState.color}`}>{billingState.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Billing Status</p>
                  {accountBalance !== null && accountBalance < 5 && (
                    <p className="text-[10px] text-amber-400 mt-1.5 font-semibold">Low Balance: ${accountBalance.toFixed(2)}</p>
                  )}
                </div>

                {/* TX Used / Remaining */}
                <div className="rounded-xl bg-muted/30 p-4" data-testid="tx-usage">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Hash className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-[11px] text-muted-foreground">TX Used</span>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    {txUsed}
                    {txLimit !== null && txLimit !== undefined && <span className="text-sm font-normal text-muted-foreground">/{txLimit}</span>}
                  </p>
                  {txRemaining !== null ? (
                    <>
                      <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                        <div className={`h-1.5 rounded-full transition-all ${txPercent > 90 ? 'bg-red-500' : txPercent > 70 ? 'bg-amber-500' : 'bg-indigo-500'}`}
                          style={{ width: `${txPercent}%` }}></div>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">{txRemaining} remaining</p>
                    </>
                  ) : (
                    <p className="text-[10px] text-muted-foreground mt-1">Unlimited</p>
                  )}
                </div>

                {/* TX Fees (%) */}
                <div className="rounded-xl bg-muted/30 p-4" data-testid="tx-fees">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Percent className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="text-[11px] text-muted-foreground">TX Fees</span>
                  </div>
                  <p className="text-xl font-bold text-indigo-400">${(usageSummary.percent_fees_total || 0).toFixed(2)}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{usageSummary.percent_fee_rate}% rate</p>
                </div>

                {/* Extra TX Charges */}
                <div className="rounded-xl bg-muted/30 p-4" data-testid="extra-tx-charges">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-[11px] text-muted-foreground">Extra TX</span>
                  </div>
                  <p className="text-xl font-bold text-amber-400">${(usageSummary.extra_tx_fees_total || 0).toFixed(2)}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{usageSummary.extra_tx_count || 0} over limit</p>
                </div>

                {/* Next Billing Date */}
                <div className="rounded-xl bg-muted/30 p-4" data-testid="next-billing-date">
                  <div className="flex items-center gap-1.5 mb-2">
                    <CalendarDays className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-[11px] text-muted-foreground">Next Billing</span>
                  </div>
                  <p className="text-lg font-bold text-foreground">
                    {usageSummary.next_billing_date ? new Date(usageSummary.next_billing_date).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : 'N/A'}
                  </p>
                  {usageSummary.billing_day && (
                    <p className="text-[10px] text-muted-foreground mt-1">Day {usageSummary.billing_day} monthly</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="border-border bg-card">
          <CardHeader><CardTitle className="text-base font-semibold">Quick Actions</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Link to="/wallets"><Button className="bg-primary text-white" data-testid="add-wallet-btn"><Wallet className="h-4 w-4 mr-2" /> Add Wallet</Button></Link>
              <Link to="/invoices"><Button variant="outline" data-testid="create-invoice-btn"><FileText className="h-4 w-4 mr-2" /> Create Invoice</Button></Link>
              <Link to="/api-keys"><Button variant="outline" data-testid="generate-api-key-btn">Generate API Key</Button></Link>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-border bg-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Pending Invoices</CardTitle>
              <Link to="/invoices"><Button variant="ghost" size="sm">View All <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
            </CardHeader>
            <CardContent>
              {recentInvoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No pending invoices</p></div>
              ) : (
                <div className="divide-y divide-border -mx-6">
                  {recentInvoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between px-6 py-3">
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-yellow-500 shrink-0" />
                        <div>
                          <p className="font-medium font-mono text-sm">{invoice.amount} {invoice.currency}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">{invoice.description || 'No description'}</p>
                        </div>
                      </div>
                      <Link to={`/checkout/${invoice.id}`} target="_blank">
                        <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-foreground">View <ArrowRight className="h-3 w-3 ml-1" /></Button>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Recent Payments</CardTitle>
              <Link to="/payments"><Button variant="ghost" size="sm">View All <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
            </CardHeader>
            <CardContent>
              {recentPayments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No payments yet</p></div>
              ) : (
                <div className="divide-y divide-border -mx-6">
                  {recentPayments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between px-6 py-3">
                      <div className="flex items-center gap-3">
                        {payment.status === 'confirmed' ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0" />
                        )}
                        <div>
                          <p className="font-medium font-mono text-sm">{payment.amount} {payment.currency}</p>
                          <p className="text-xs text-muted-foreground font-mono truncate max-w-[150px]">{payment.tx_hash?.slice(0, 10)}...</p>
                        </div>
                      </div>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                        payment.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-yellow-500/10 text-yellow-500'
                      }`}>{payment.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MerchantLayout>
  );
};

export default MerchantDashboard;
